import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip } from '@mui/material';
import theme from '../../theme';
import { getAgentCallsData } from '../../utils/apis';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import React from 'react';
import CallsDataSkeleton from './Skeletons/CallsDataSkeleton';
import NoDataFallback from '../common/NoDataFallback';
import { Eye as EyeIcon, Prohibit } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../hooks/useReduxTypeScript';
import {
  setPaginationModel,
  setFilterModel,
  setSortModel,
} from '../../utils/redux/slices/callsPageSlice';
interface ICallsData {
  agentId: string;
  fromDate: string;
  toDate: string;
  showAlert?: (message: string, severity: 'success' | 'error') => void;
}

const CallsData = ({ agentId, fromDate, toDate }: ICallsData) => {
  const dispatch = useAppDispatch();
  const { paginationModel, filterModel, sortModel } = useAppSelector(
    state => state.callsPage
  );
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'agentCallsData',
      agentId,
      fromDate,
      toDate,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () =>
      getAgentCallsData({
        agentId,
        fromDate,
        toDate,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      }),
    enabled: Boolean(agentId || fromDate || toDate),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 1,
    retry: 0,
    gcTime: 1000 * 60 * 10,
  });

  const handleCallDetails = (row: any) => {
    navigate(`/calls/${row.callId}?callSid=${row.callSid}&agentId=${agentId}`);
  };

  const handlePaginationModelChange = (model: any) => {
    dispatch(setPaginationModel(model));
  };

  const handleFilterModelChange = (model: any) => {
    dispatch(setFilterModel(model));
  };

  const handleSortModelChange = (model: any) => {
    dispatch(setSortModel(model));
  };

  const renderNumberCell = (
    value: string,
    blacklistedNumber: string | null
  ) => {
    const isBlocked = blacklistedNumber != null && blacklistedNumber === value;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <span>{value || '-'}</span>
        {isBlocked && (
          <Prohibit
            size={14}
            color="#E74C3C"
            style={{ flexShrink: 0 }}
            aria-label="Blocked"
          />
        )}
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'from',
      headerName: 'From',
      headerClassName: 'data-grid-header',
      width: 130,
      filterable: true,
      renderCell: (params: any) => (
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            handleCallDetails(params.row);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              handleCallDetails(params.row);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {renderNumberCell(params.value, params.row.blacklistedNumber)}
        </Box>
      ),
    },
    {
      field: 'to',
      headerName: 'To',
      headerClassName: 'data-grid-header',
      width: 130,
      filterable: true,
      renderCell: (params: any) => (
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            handleCallDetails(params.row);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              handleCallDetails(params.row);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {renderNumberCell(params.value, params.row.blacklistedNumber)}
        </Box>
      ),
    },
    {
      field: 'callType',
      headerName: 'Call Type',
      headerClassName: 'data-grid-header',
      width: 130,
      filterable: true,
    },
    {
      field: 'callStartedAt',
      headerName: 'Call Start Time',
      headerClassName: 'data-grid-header',
      width: 180,
      filterable: true,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        } catch {
          return '-';
        }
      },
    },
    {
      field: 'callEndedAt',
      headerName: 'Call End Time',
      headerClassName: 'data-grid-header',
      width: 180,
      filterable: true,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        } catch {
          return '-';
        }
      },
    },
    {
      field: 'endReason',
      headerName: 'End Reason',
      headerClassName: 'data-grid-header',
      width: 150,
      filterable: true,
      renderCell: (params: any) => {
        const value = params.value;

        if (!value) {
          return (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              -
            </Box>
          );
        }

        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      field: 'transferred',
      headerName: 'Transferred',
      headerClassName: 'data-grid-header',
      width: 120,
      filterable: true,
      align: 'center',
      renderCell: (params: any) => {
        if (params.value == null) {
          return '-';
        }

        return params.value ? 'Yes' : 'No';
      },
    },
    {
      field: 'reason',
      headerName: 'Transfer Reason',
      headerClassName: 'data-grid-header',
      width: 220,
      filterable: true,
      renderCell: (params: any) => {
        const value = params.value;

        if (!value) {
          return (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              -
            </Box>
          );
        }

        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      field: 'callDuration',
      headerName: 'Billed Duration(sec)',
      headerClassName: 'data-grid-header',
      width: 140,
      filterable: true,
      align: 'center',
      type: 'number',
      renderCell: (params: any) => {
        if (params.value == null) return '-';
        try {
          const seconds = Number(params.value);
          const remainder = seconds % 60;
          const rounded =
            remainder <= 10
              ? Math.floor(seconds / 60) * 60
              : Math.ceil(seconds / 60) * 60;
          return rounded;
        } catch {
          return '-';
        }
      },
    },
    {
      field: 'callSid',
      headerName: 'Call SID',
      headerClassName: 'data-grid-header',
      width: 220,
      filterable: true,
    },
    {
      field: 'callId',
      headerName: 'Call ID',
      headerClassName: 'data-grid-header',
      width: 220,
      filterable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerClassName: 'data-grid-header',
      align: 'center',
      headerAlign: 'center',
      width: 80,
      sortable: false,
      renderCell: params => (
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleCallDetails(params.row);
            }}
            aria-label="View call details"
          >
            <EyeIcon size={20} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Transform data to ensure unique row IDs
  const transformedData = React.useMemo(() => {
    return (
      data?.data?.calls?.map((call: any, index: number) => ({
        ...call,
        uniqueId: `${call.callSid}-${index}`,
      })) || []
    );
  }, [data?.data?.calls]);

  if (isLoading) {
    return <CallsDataSkeleton />;
  }

  if (isError) {
    return (
      <NoDataFallback
        message="Error fetching calls data"
        description="Please try again later."
      />
    );
  }

  if (!data || data?.data?.calls?.length === 0) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: 'calc(100vh - 280px)',
          minHeight: 400,
          width: '100%',
          bgcolor: '#fff',
        }}
      >
        <NoDataFallback
          message="No calls data available"
          description="There has not been any calls made for this agent in the selected date range."
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: 'calc(100vh - 280px)',
        minHeight: 400,
        width: '100%',
        bgcolor: '#fff',
      }}
    >
      <DataGrid
        rows={transformedData}
        columns={columns}
        getRowId={row => row.uniqueId}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
        paginationMode="server"
        rowCount={data?.data?.totalCalls || 0}
        disableRowSelectionOnClick
        loading={isLoading}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        sx={{
          border: `1px solid ${theme.palette.lightGray.main}`,
          borderRadius: '12px',
          height: '100%',
          flex: 1,
          '& .data-grid-header': {
            bgcolor: '#F8F9FC',
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 600,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            '&:focus, &:focus-within': {
              outline: 'none !important',
            },
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
        }}
      />
    </Box>
  );
};

export default CallsData;
